import {
  Box,
  Button,
  CreatableSingleSelect,
  Flex,
  FormControl,
  FormLabel,
  H3,
} from '@ouster/webviz-react-components';

import { useMemo, useState } from 'react';
import { OrganizationInfo } from 'src/types';

import { OrgOption } from '../types';

interface TopologyStepProps {
  orgOptions: OrgOption[];
  survivorId: number | null;
  sourceIds: number[];
  topologyError: string | null;
  isReadOnly?: boolean;
  onSurvivorChange: (next: number | null) => void;
  onSourcesChange: (next: number[]) => void;
}

export default function TopologyStep({
  orgOptions,
  survivorId,
  sourceIds,
  topologyError,
  isReadOnly = false,
  onSurvivorChange,
  onSourcesChange,
}: TopologyStepProps) {
  const [hasTopologyChange, setHasTopologyChange] = useState(false);
  const [pendingSourceRows, setPendingSourceRows] = useState(0);

  const getOrgOptionById = (orgId: number): OrgOption | null =>
    orgOptions.find((option) => option.data.org_id === orgId) ?? null;

  const survivor = survivorId ? getOrgOptionById(survivorId) : null;

  const selectedSourceValues = useMemo(() => new Set(sourceIds), [sourceIds]);

  const sourceRows: (number | null)[] = useMemo(() => {
    const initialEmptyRowCount = sourceIds.length === 0 ? 1 : 0;
    const emptyRowCount = pendingSourceRows + initialEmptyRowCount;

    return [...sourceIds, ...Array.from({ length: emptyRowCount }, () => null)];
  }, [pendingSourceRows, sourceIds]);

  const getOrgOptionId = (item: OrganizationInfo | string): number | null => {
    if (typeof item === 'string') {
      return (
        orgOptions.find(
          (option) => option.value === item || option.label === item
        )?.data.org_id ?? null
      );
    }

    return (
      orgOptions.find((option) => option.data.org_id === item.org_id)?.data
        .org_id ?? null
    );
  };

  const getSourceOptionsForRow = (currentId: number | null): OrgOption[] =>
    orgOptions.filter((option) => {
      if (survivorId && option.data.org_id === survivorId) {
        return false;
      }

      if (currentId && option.data.org_id === currentId) {
        return true;
      }

      return !selectedSourceValues.has(option.data.org_id);
    });

  const availableSourceCount = orgOptions.filter((option) => {
    if (survivorId && option.data.org_id === survivorId) {
      return false;
    }
    return !selectedSourceValues.has(option.data.org_id);
  }).length;

  const hasEmptySourceRow = sourceRows.some((sourceRow) => !sourceRow);
  const showAddSourceButton = !hasEmptySourceRow && availableSourceCount > 0;

  const showTopologyError = hasTopologyChange && Boolean(topologyError);

  const formatOrgOptionLabel = (option: unknown) => (option as OrgOption).label;

  const removeSourceAtIndex = (index: number, isExistingSource: boolean) => {
    if (isExistingSource) {
      onSourcesChange(
        sourceIds.filter((_, sourceIndex) => sourceIndex !== index)
      );
      return;
    }

    setPendingSourceRows((count) => Math.max(0, count - 1));
  };

  const handleSurvivorOptionChange = (
    item: OrganizationInfo | string | null
  ) => {
    setHasTopologyChange(true);

    if (!item) {
      onSurvivorChange(null);
      return;
    }

    const selected = getOrgOptionId(item);
    onSurvivorChange(selected);
  };

  const handleSourceOptionChange = (
    index: number,
    isExistingSource: boolean,
    item: OrganizationInfo | string | null
  ) => {
    setHasTopologyChange(true);

    if (!item) {
      removeSourceAtIndex(index, isExistingSource);
      return;
    }

    const selected = getOrgOptionId(item);
    if (!selected) {
      return;
    }

    if (isExistingSource) {
      const nextSources = [...sourceIds];
      nextSources[index] = selected;
      onSourcesChange(nextSources);
      return;
    }

    onSourcesChange([...sourceIds, selected]);
    setPendingSourceRows((count) => Math.max(0, count - 1));
  };

  return (
    <Box
      data-testid="merge-step-topology"
      display="grid"
      alignContent="start"
      rowGap={4}
      h="50vh"
      pr={6}
    >
      <Box>
        <H3 fontSize={26} mb={2}>
          Organization Topology
        </H3>

        <Box
          fontSize={14}
          mb={4}
          color={showTopologyError ? 'yellow.200' : 'blue.200'}
          data-testid={
            showTopologyError ? 'merge-topology-error' : 'merge-topology-info'
          }
        >
          {showTopologyError
            ? topologyError
            : survivor
            ? 'Add more source organizations to merge as needed.'
            : 'Define which organization survives and which will be merged.'}
        </Box>
      </Box>

      <Box overflow="auto" display="grid" rowGap={6} alignContent="start">
        <FormControl paddingInline={0}>
          <FormLabel
            textTransform="uppercase"
            fontWeight={600}
            letterSpacing="0.05em"
          >
            Survivor (Target)
          </FormLabel>
          <CreatableSingleSelect
            placeholderText="Select survivor organization"
            options={orgOptions}
            selectedOption={survivor}
            isDisabled={isReadOnly}
            onOptionChange={handleSurvivorOptionChange}
            formatOptionLabel={formatOrgOptionLabel}
            data-testid="merge-survivor-select"
          />
        </FormControl>

        {survivor && (
          <FormControl paddingInline={0}>
            <FormLabel
              textTransform="uppercase"
              fontWeight={600}
              letterSpacing="0.05em"
            >
              Sources (To Merge & Deactivate)
            </FormLabel>
            <Box display="grid" rowGap={3}>
              {sourceRows.map((source, index) => {
                const sourceOptions = getSourceOptionsForRow(source);
                const isExistingSource = index < sourceIds.length;
                const selectedSource = source ? getOrgOptionById(source) : null;

                const showRemoveButton =
                  isExistingSource || pendingSourceRows > 0;

                return (
                  <Flex
                    key={selectedSource?.value ?? `source-row-${index}`}
                    gap={2}
                  >
                    <Box flex={1}>
                      <CreatableSingleSelect
                        placeholderText="Select source organization"
                        options={sourceOptions}
                        selectedOption={selectedSource}
                        isDisabled={isReadOnly}
                        onOptionChange={(item) => {
                          handleSourceOptionChange(
                            index,
                            isExistingSource,
                            item
                          );
                        }}
                        formatOptionLabel={formatOrgOptionLabel}
                        data-testid={`merge-source-select-${index}`}
                      />
                    </Box>
                    {showRemoveButton ? (
                      <Button
                        type="button"
                        colorScheme="gray"
                        disabled={isReadOnly}
                        onClick={() => {
                          setHasTopologyChange(true);
                          removeSourceAtIndex(index, isExistingSource);
                        }}
                        data-testid={`merge-source-remove-${index}`}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </Flex>
                );
              })}

              {showAddSourceButton ? (
                <Button
                  type="button"
                  colorScheme="gray"
                  disabled={isReadOnly}
                  width="fit-content"
                  onClick={() => {
                    setHasTopologyChange(true);
                    setPendingSourceRows((count) => count + 1);
                  }}
                  data-testid="merge-source-add"
                  mt={8}
                  w="full"
                  p={4}
                  height="48px"
                  rounded={6}
                  border="1px dashed"
                  borderColor="blue.300"
                  justifyContent="left"
                >
                  + Add source organization
                </Button>
              ) : null}
            </Box>
          </FormControl>
        )}
      </Box>
    </Box>
  );
}
